import { useMemo, createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

const authenticateUser = async (username, password) => {
  const url = 'https://eu-api.empathicbuilding.com/v1/login';
  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: `email=${encodeURIComponent(username)}&password=${encodeURIComponent(
      password
    )}`,
  };

  const response = await fetch(url, requestOptions);
  if (response.status !== 200) {
    return false;
  }

  const data = await response.json();
  if (data.token) {
    return data.token;
  }
};

const fetchUserRole = async (token) => {
  const url = 'https://eu-api.empathicbuilding.com/v1/settings';
  const requestOptions = {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await fetch(url, requestOptions);
    const data = await response.json();
    
    // Check if the new roles array format exists
    if (data.user.roles && Array.isArray(data.user.roles) && data.user.roles.length > 0) {
      // Extract all roles from all role objects
      const allRoles = [];
      data.user.roles.forEach(roleObject => {
        const roleValues = Object.values(roleObject);
        allRoles.push(...roleValues);
      });
      return allRoles;
    }
    
    // Fallback to old format for backward compatibility - return as array for consistency
    return data.user.role ? [data.user.role] : [];
  } catch (e) {
    console.error(e);
  }
  return [];
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [token, setToken] = useState();
  const [userRoles, setUserRoles] = useState([]);

  const login = async (username, password) => {
    const userToken = await authenticateUser(username, password);
    if (userToken) {
      setToken(userToken);
      await window.localStorage.setItem('token', JSON.stringify(userToken));
      return userToken;
    }
  };

  const logout = async () => {
    setToken();
    await window.localStorage.removeItem('token');
    navigate('/login');
  };

  useEffect(() => {
    const fetchTokenFromLocalStorage = async () => {
      const response = await window.localStorage.getItem('token');
      const token = JSON.parse(response);
      setToken(token);
    };
    fetchTokenFromLocalStorage();
  }, []);

  useEffect(() => {
    const updateUserRoles = async () => {
      if (!token) return;

      const roles = await fetchUserRole(token);
      setUserRoles(roles);
    };
    updateUserRoles();
  }, [token]);

  const value = useMemo(
    () => ({
      token,
      login,
      logout,
      userRoles,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, userRoles]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
